import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Text, Col, FlexboxGrid } from 'rsuite';
import first from 'lodash/first';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import { getUsersActionableTaxPaymentPeriodsAsync } from '../../../store/slices/dashboard/dashboardThunks';
import {
  selectConfigExpressBulkRenewEnabled,
  selectConfigExpressRegistrationRenewEnabled,
  selectConfigExpressRegistrationVerbiage,
  selectConfigExpressTaxEnabled,
} from '../../../store/slices/config/configSelectors';
import {
  selectUsersTaxableLicensePeriods,
  selectUsersTaxableLicensePeriodsInProgress,
  selectUsersTaxableLicensePeriodsSuccess,
} from '../../../store/slices/dashboard/dashboardSelectors';
import { DASHBOARD_ACTIONS_ENUM } from '../../../common/enums';
import { getAddressWithUnitNumber } from '../../../common/utils';
import { useAppDispatch } from 'src/store/store';
import { useResize } from 'src/hooks/useResize';
import { License } from 'src/types/licenses';
import { EndpointData } from 'src/types/store';
import ActionItem, {
  ActionItemAction,
  ActionItemSelectedLicense,
} from './ActionItem';
import IconIndicator from '../../user-feedback/IconIndicator';
import Notice from '../../user-feedback/Notice';

type DashboardAction = ActionItemAction;

interface ActionsListProps {
  renewableLicensesData: EndpointData<License>;
  contentSectionHeight: number;
}

const ActionsList = ({
  renewableLicensesData,
  contentSectionHeight,
}: ActionsListProps) => {
  const dispatch = useAppDispatch();
  const { isMobileOrTablet } = useResize();

  const renewEnabled = useSelector(selectConfigExpressRegistrationRenewEnabled);
  const bulkRenewEnabled = useSelector(selectConfigExpressBulkRenewEnabled);
  const {
    data: renewableLicenses,
    loading: renewableLicensesInProgress,
    error: renewableLicensesError,
  } = renewableLicensesData;

  const taxEnabled = useSelector(selectConfigExpressTaxEnabled);
  const taxablePeriods = useSelector(selectUsersTaxableLicensePeriods);
  const taxablePeriodsSuccess = useSelector(
    selectUsersTaxableLicensePeriodsSuccess
  );
  const taxablePeriodsInProgress = useSelector(
    selectUsersTaxableLicensePeriodsInProgress
  );
  const registrationVerbiage = useSelector(
    selectConfigExpressRegistrationVerbiage
  );

  useEffect(() => {
    if (taxEnabled) {
      dispatch(getUsersActionableTaxPaymentPeriodsAsync());
    }
  }, [dispatch, taxEnabled]);

  const generateTaxActions = useCallback((): DashboardAction[] => {
    return (
      taxablePeriods?.map((period) => {
        let label = `Pay Tax (${period.name}) for ${period.licenses.length} properties`;

        let selectedLicense: ActionItemSelectedLicense | null = null;

        // if there is only one actionable permit for the period set the selctedLicense, so that onClick we go to the report revenue page rather than the select properties page
        if (period.licenses.length <= 1) {
          const license = first(period.licenses);
          if (license) {
            const address = getAddressWithUnitNumber(
              license.address,
              license.unitNumber
            );
            label = `Pay Tax (${period.name}) ${address}`;
            selectedLicense = {
              address,
              licenseNumber: license.license ?? '',
            };
          }
        }

        return {
          label,
          startDate: period.startDate,
          endDate: period.endDate,
          dueDate: period.dueDate,
          type: DASHBOARD_ACTIONS_ENUM.BULK_TAX,
          selectedLicense,
          periodLabel: period.name,
        };
      }) ?? []
    );
  }, [taxablePeriods]);

  const generateRenewalActions = useCallback((): DashboardAction[] => {
    return (
      renewableLicenses?.map((license) => {
        const fullAddress = getAddressWithUnitNumber(
          license.property_address,
          license.unit_number
        );
        const label = `Renew ${registrationVerbiage} ${fullAddress}`;

        return {
          label,
          dueDate: license.expiration_date,
          selectedLicense: {
            address: fullAddress,
            licenseNumber: license.license ?? '',
            applicationNumber: license.application_number,
          },
          type:
            bulkRenewEnabled && renewableLicenses.length > 1
              ? DASHBOARD_ACTIONS_ENUM.BULK_RENEW
              : DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW,
        };
      }) ?? []
    );
  }, [renewableLicenses, registrationVerbiage, bulkRenewEnabled]);

  const taxActions = useMemo(() => {
    if (taxEnabled && taxablePeriods) {
      return generateTaxActions();
    }
    return [];
  }, [taxEnabled, taxablePeriods, generateTaxActions]);

  const renewalActions = useMemo(() => {
    if (renewEnabled && renewableLicenses) {
      return generateRenewalActions();
    }
    return [];
  }, [renewEnabled, renewableLicenses, generateRenewalActions]);

  const actions = useMemo(() => {
    const allActions = [...taxActions, ...renewalActions];

    //order by due date desc
    return sortBy(allActions, ({ dueDate }) => dayjs(dueDate).unix());
  }, [taxActions, renewalActions]);

  const isLoading = useMemo(() => {
    const loadingRenewals = renewEnabled && renewableLicensesInProgress;
    const loadingTax = taxEnabled && taxablePeriodsInProgress;

    return loadingRenewals || loadingTax;
  }, [
    renewEnabled,
    renewableLicensesInProgress,
    taxEnabled,
    taxablePeriodsInProgress,
  ]);

  const renderActionRows = useCallback(() => {
    const firstBulkRenewIndex = actions.findIndex(
      (item) => item.type === DASHBOARD_ACTIONS_ENUM.BULK_RENEW
    );
    const bulkRenewCount = actions.filter(
      (item) => item.type === DASHBOARD_ACTIONS_ENUM.BULK_RENEW
    ).length;

    return actions.map((action, index) => {
      if (action.type !== DASHBOARD_ACTIONS_ENUM.BULK_RENEW) {
        return (
          <ActionItem
            key={`action_item_${action.type}_${index}`}
            action={action}
          />
        );
      }

      if (index !== firstBulkRenewIndex) return null;

      return (
        <ActionItem
          key="action_item_bulk_renew"
          action={{
            type: DASHBOARD_ACTIONS_ENUM.BULK_RENEW,
            label: `${action.label} + ${bulkRenewCount - 1} properties`,
            dueDate: action.dueDate,
          }}
        />
      );
    });
  }, [actions]);

  const renderEmptyState = () => {
    if (isLoading) {
      return (
        <div className="pv-24 pv-48">
          <IconIndicator />
        </div>
      );
    }

    const isRenewalsEvaluationSucceeded =
      !renewEnabled || renewableLicensesError === undefined;
    const isTaxEvaluationSucceeded = !taxEnabled || taxablePeriodsSuccess;

    if (isRenewalsEvaluationSucceeded && isTaxEvaluationSucceeded) {
      return (
        <div
          role="cell"
          className="ph-24 pv-48 text-center"
        >
          <Text as="i">No outstanding actions.</Text>
        </div>
      );
    }

    return (
      <div
        role="cell"
        className="ph-24 pv-48"
      >
        <Notice message="Failed to retrieve data."></Notice>
      </div>
    );
  };

  const renderDesktopActionsPanel = () => (
    <>
      <FlexboxGrid
        align={'middle' as const}
        className="actions-panel-header-container"
        role="row"
      >
        <FlexboxGrid.Item
          align="start"
          style={{ borderTopLeftRadius: '4px' }}
          className="pl-24 pv-8"
          as={Col}
          colspan={24}
          lg={16}
          xsHidden
          smHidden
          mdHidden
        >
          <Text>Action</Text>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          align="start"
          className="pv-8"
          as={Col}
          colspan={4}
          xsHidden
          smHidden
          mdHidden
        >
          <Text>Due by</Text>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          align="start"
          style={{ borderTopRightRadius: '4px' }}
          className="pr-24 pv-8"
          as={Col}
          colspan={4}
          xsHidden
          smHidden
          mdHidden
        >
          &nbsp;
        </FlexboxGrid.Item>
      </FlexboxGrid>
      <div
        role="grid"
        className="dashboard-actions-panel__scroll scroll-bar grdGreyStripe"
      >
        {renderActionRows()}
      </div>
    </>
  );

  const renderMobileActionsPanel = () => (
    <FlexboxGrid.Item
      className="ph-0 pv-0"
      colspan={24}
    >
      <div role="cell">
        <div role="grid">
          <FlexboxGrid
            align={'middle' as const}
            role="row"
          >
            <FlexboxGrid.Item
              align="start"
              style={{ borderTopLeftRadius: '4px' }}
              className="pl-24 pv-8 actions-panel-header-container"
              as={Col}
              colspan={24}
              lg={16}
              xsHidden
              smHidden
              mdHidden
            >
              <Text>Action</Text>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              align="start"
              className="pv-8 actions-panel-header-container"
              as={Col}
              colspan={4}
              xsHidden
              smHidden
              mdHidden
            >
              <Text>Due by</Text>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              align="start"
              style={{ borderTopRightRadius: '4px', height: '100%' }}
              className="pr-24 pv-8 actions-panel-header-container"
              as={Col}
              colspan={4}
              xsHidden
              smHidden
              mdHidden
            >
              &nbsp;
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              align="start"
              as={Col}
              colspan={24}
              className="grdGreyStripe ph-0"
            >
              {renderActionRows()}
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </div>
      </div>
    </FlexboxGrid.Item>
  );

  const contentSectionStyle = useMemo(
    () =>
      isMobileOrTablet
        ? undefined
        : {
            flex: 1,
            minHeight: contentSectionHeight,
            display: 'flex' as const,
            flexDirection: 'column' as const,
            overflow: 'hidden',
          },
    [isMobileOrTablet, contentSectionHeight]
  );

  const contentInnerStyle = useMemo(
    () =>
      isMobileOrTablet
        ? undefined
        : {
            flex: 1,
            display: 'flex' as const,
            flexDirection: 'column' as const,
            minHeight: 0,
            overflow: 'hidden',
            justifyContent: isEmpty(actions)
              ? ('center' as const)
              : ('flex-start' as const),
          },
    [isMobileOrTablet, actions]
  );

  return (
    <FlexboxGrid
      role="grid"
      className="dashboard-actions-panel"
    >
      <FlexboxGrid.Item
        colspan={24}
        className="mb-8"
      >
        <div role="cell">
          <Text
            as="h2"
            className="text-large text-bold"
          >
            Actions Required
          </Text>
        </div>
      </FlexboxGrid.Item>

      <FlexboxGrid.Item
        colspan={24}
        className="mb-8"
        style={{ height: 24, overflow: 'hidden' }}
        aria-hidden="true"
      >
        <div role="cell" />
      </FlexboxGrid.Item>

      <FlexboxGrid.Item
        colspan={24}
        className="content-section-no-padding dashboard-actions-panel__content"
        style={contentSectionStyle}
      >
        <div
          role="cell"
          style={contentInnerStyle}
        >
          {isEmpty(actions) ? (
            renderEmptyState()
          ) : isMobileOrTablet ? (
            <div role="grid">{renderMobileActionsPanel()}</div>
          ) : (
            renderDesktopActionsPanel()
          )}
        </div>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default ActionsList;
